import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/material';
import _ from 'lodash';

// Local imports
import { App } from 'BMapsSrc/BMapsApp';
import { DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import { createJob } from 'BMapsRedux/jobs/thunks';
import { useDispatch } from 'BMapsRedux';
import { jobDefinitions, JobTypes } from 'BMapsSrc/jobs/JobDefinition';
import { UserActions } from 'BMapsSrc/cmds';
import { TextArea } from 'BMapsUI/common/TextFields';
import Select from 'BMapsUI/common/Select';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { wrapForDisabledTooltip } from 'BMapsUI/common/helpers';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import LinkLikeButton from 'BMapsUI/common/LinkLikeButton';
import {
    ImportErrors, PlaceHolder,
} from '../../import_components';
import { BasicTextField } from '../../../SimulationPane/Components';

export default function CompoundGenerationTab({ initData }) {
    const dispatch = useDispatch();
    const [
        moleculeGenerationPrograms, setMoleculeGenerationPrograms,
    ] = useState([JobTypes.REINVENT]);
    const [chosenProgram, setChosenProgram] = useState(moleculeGenerationPrograms[0]);
    const [compounds, setCompounds] = useState(App.Workspace.getLoadedCompounds());
    const [errors, setErrors] = useState([]);
    const [molDataText, setMolDataText] = useState(
        initData?.selectedCompounds?.map((x) => x.getSmiles()).join('\n')
        || initData?.molDataText
        || ''
    );
    const [jobName, setJobName] = useState(initData?.jobName || '');
    const [isSubmitting, setIsSubmitting] = useState(false);

    async function generateCompounds(compoundSmiles) {
        const smiles = Array.isArray(compoundSmiles) ? compoundSmiles : [compoundSmiles];
        const validationErrs = validateInput(smiles, chosenProgram);
        if (validationErrs.length > 0) {
            setErrors(validationErrs);
            return false;
        }

        setIsSubmitting(true);
        const { results, error } = await dispatch(
            createJob(JobTypes.REINVENT, { smiles, jobName })
        );
        setIsSubmitting(false);
        if (results) {
            setMolDataText('');
            setJobName('');
            setChosenProgram(moleculeGenerationPrograms[0]);
            setErrors([]);
            UserActions.OpenImport('jobs_compounds_tab', { jobid: results?.jobid });
            return true;
        } else {
            setErrors([`The job submission failed: ${error || 'Unspecified error'}`]);
            return false;
        }
    }

    function validateInput(input, program) {
        if (program === JobTypes.REINVENT) {
            // Allowed bracketed tokens for REINVENT mol2mol_scaffold_generic model
            const allowedTokens = new Set([
                '[11CH3]',
                '[11C]',
                '[11c]',
                '[123I]',
                '[18F]',
                '[19F]',
                '[76Br]',
                '[C@@H]',
                '[C@@]',
                '[C@H]',
                '[C@]',
                '[N+]',
                '[N@+]',
                '[N@@+]',
                '[O-]',
                '[O]',
                '[S@@]',
                '[S@]',
                '[n+]',
                '[n-]',
                '[nH]',
            ]);

            const unsupported = input.flatMap((s) => {
                // Extract SMILES part (before any whitespace/name)
                const smilesOnly = s.split(/\s/)[0];

                // Find all bracketed tokens
                const bracketedTokens = smilesOnly.match(/\[[^\]]+\]/g) || [];

                // Find tokens that are not in the allowed list
                const badTokens = bracketedTokens.filter((token) => !allowedTokens.has(token));

                return badTokens.length > 0 ? [{ smiles: s, groups: badTokens }] : [];
            });

            return unsupported.map(({ smiles, groups }) => `Unsupported tokens (${groups.join(', ')}) in SMILES: ${smiles}`);
        }
        return [];
    }

    const handleCompoundSelect = (compound) => {
        const newInput = compound.getSmiles();
        if (!molDataText || molDataText === '') {
            setMolDataText(newInput);
            return;
        }
        setMolDataText(`${molDataText}\n${newInput}`);
    };
    // Update the Submit button (disabled / tooltip) as necessary
    let submitBtnTooltip = 'Click to submit compound generation job';
    let disabled = false;

    if (isSubmitting) {
        submitBtnTooltip = 'Submitting job...';
        disabled = true;
    } else if (!molDataText || molDataText === '') {
        submitBtnTooltip = 'Include compound generation inputs above before submission';
        disabled = true;
    }

    useEffect(() => {
        setCompounds(App.Workspace.getLoadedCompounds().filter(
            (compound) => !molDataText.includes(compound.getUnnamedSmiles())
        ));
    }, [molDataText]);

    return (
        <div>
            <DialogContentSection title="Compound Generation" disablePaddingBottom>
                Derive new compounds with an AI compound generation program.
            </DialogContentSection>
            <DialogContentSection
                title="1. Specify Compound Input (SMILES)"
                disablePaddingTop
            >
                {compounds.length > 0 && (
                    <Select
                        label="Add Workspace Compounds"
                        SelectProps={{
                            native: false,
                        }}
                        sx={{ width: '50%', margin: '1rem 0' }}
                    >
                        {compounds.map((compound) => (
                            <MenuItem
                                key={compound.smiles}
                                onClick={() => {
                                    handleCompoundSelect(compound);
                                }}
                            >
                                {compound.displayName}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                <CompoundInputBox
                    value={molDataText}
                    setValues={(e) => setMolDataText(e)}
                />
                <Select
                    label="Molecule Generation Program"
                    SelectProps={{
                        native: false,
                    }}
                    value={chosenProgram}
                    onChange={(e) => {
                        setChosenProgram(e.target.value);
                    }}
                    sx={{ width: '50%', marginTop: '1.5rem' }}
                >
                    {moleculeGenerationPrograms.map((program) => (
                        <MenuItem key={program} value={program}>
                            {jobDefinitions[program].getJobTypeName()}
                        </MenuItem>
                    ))}
                </Select>
                <Box sx={{ width: '50%' }}>
                    <BasicTextField
                        labelText="Job Name (optional)"
                        value={jobName}
                        title="Job Name (optional)"
                        required={false}
                        onChange={(evt) => setJobName(evt.target.value)}
                    />
                </Box>
            </DialogContentSection>
            <DialogContentSection
                title="2. Submit Compound Generation Job"
                disablePaddingBottom
            >
                <Box>
                    <BMapsTooltip title={submitBtnTooltip}>
                        {wrapForDisabledTooltip(
                            <DialogPrimaryButton
                                onClick={() => {
                                    const smiles = molDataText
                                        .split('\n')
                                        .map((s) => s.trim())
                                        .filter((s) => s !== '');
                                    generateCompounds(smiles);
                                }}
                                style={{ width: '100%' }}
                                disabled={disabled}
                            >
                                Submit for Compound Generation
                            </DialogPrimaryButton>
                        )}
                    </BMapsTooltip>
                    <span style={{ fontStyle: 'italic' }}>
                        Completed jobs will be will be available in the
                        {' '}
                        <LinkLikeButton
                            onClick={() => UserActions.OpenImport('jobs_compounds_tab')}
                                            // Unsetting vertical-align allows the button text to
                                            // line up nicely with the rest of the test.
                            style={{ padding: 0, verticalAlign: 'unset' }}
                        >
                            Compound Jobs
                        </LinkLikeButton>
                        {' '}
                        tab.
                        <br />
                        Load a completed job to view the generated compounds
                        and import them into your workspace.
                    </span>

                </Box>
            </DialogContentSection>
            {!!errors && errors.length > 0 && <ImportErrors errors={errors} /> }
        </div>
    );
}

function CompoundInputBox({
    value, setValues,
}) {
    return (
        <Box sx={{ marginTop: '1rem' }}>
            <PlaceHolder $show={value === ''}>
                <>
                    Enter ligand smiles, one per line,
                    <br />
                    or select a compound from the workspace.
                </>
            </PlaceHolder>
            <TextArea
                autoFocus
                multiline
                minRows={1}
                value={value}
                onChange={(e) => setValues(e.target.value)}
                sx={{
                    '& textarea': {
                        resize: 'vertical',
                        minHeight: '6rem',
                    },
                }}
            />
        </Box>
    );
}
